import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { css, keyframes } from '@emotion/react';
import { colors, fontStyles, spacing, safeAreaInset } from '../../styles';
import GiftIcon from '../../assets/Icons/Gift.svg';

interface OneTimeOfferScreenProps {
  onClose: () => void;
  onRedeemOffer: () => void;
  onExploreFree: () => void;
  originalAnnualPrice: number;
  discountAnnualPrice: number;
  currencySymbol: string;
  isLoading: boolean;
  /** When true, only the redeem CTA is disabled (e.g. offerings haven't loaded yet). Close and explore-free remain interactive. */
  isRedeemDisabled?: boolean;
  isSkipUser?: boolean;
  freeTrialDays?: number;
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const OneTimeOfferScreen: React.FC<OneTimeOfferScreenProps> = ({
  onClose,
  onRedeemOffer,
  onExploreFree,
  originalAnnualPrice,
  discountAnnualPrice,
  currencySymbol,
  isLoading,
  isRedeemDisabled = false,
  isSkipUser = false,
  freeTrialDays = 7,
}) => {
  const { t } = useTranslation();
  const savingsPercentage =
    originalAnnualPrice > 0 ? Math.round((1 - discountAnnualPrice / originalAnnualPrice) * 100) : 0;
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isLoading, onClose]);

  return (
    <div
      css={css`
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 1000;
        background-color: ${colors.snow};
        html[data-theme='dark'] & {
          background-color: ${colors.liquorice};
        }
        display: flex;
        flex-direction: column;
        padding: ${spacing.regular};
        overflow-y: auto;
        animation: ${fadeIn} 0.3s ease-out;
      `}
    >
      {/* Close button */}
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          width: 100%;
          padding: ${spacing.deci} 0;
          padding-top: ${safeAreaInset.top};
        `}
      >
        <button
          ref={closeButtonRef}
          onClick={onClose}
          disabled={isLoading}
          aria-label={t('subscription.closeOfferAriaLabel')}
          css={css`
            background: none;
            border: none;
            font-size: 24px;
            cursor: pointer;
            color: ${colors.silver};
            html[data-theme='dark'] & {
              color: ${colors.sugar};
            }
            &:disabled {
              cursor: not-allowed;
            }
          `}
        >
          ✕
        </button>
      </div>

      {/* Content area */}
      <div
        css={css`
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          max-width: 400px;
          margin: 0 auto;
        `}
      >
        {/* Gift icon */}
        <div
          css={css`
            width: 64px;
            height: 64px;
            border-radius: 50%;
            background: ${colors.petrolBlue};
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: ${spacing.deci};
          `}
        >
          <img src={GiftIcon} alt={t('subscription.giftAlt')} width={28} height={28} />
        </div>

        {/* WELCOME GIFT pill */}
        <span
          css={css`
            display: inline-block;
            background: ${colors.petrolBlue};
            color: ${colors.sugar};
            ${fontStyles.roboMouse}
            text-transform: uppercase;
            letter-spacing: 1px;
            padding: 4px 12px;
            border-radius: 16px;
            margin-bottom: ${spacing.deci};
          `}
        >
          {t('subscription.welcomeGift')}
        </span>

        {/* Headline */}
        <h1
          css={css`
            ${fontStyles.elephant}
            color: ${colors.liquorice};
            html[data-theme='dark'] & {
              color: ${colors.sugar};
            }
            text-align: center;
            margin: 0;
            margin-bottom: ${isSkipUser ? spacing.centi : spacing.deci};
          `}
        >
          {isSkipUser ? t('subscription.skipOfferHeadline') : t('subscription.oneTimeOffer')}
        </h1>

        {/* Subtitle (skip users only) */}
        {isSkipUser && (
          <p
            css={css`
              ${fontStyles.shark}
              color: ${colors.petrolBlue};
              html[data-theme='dark'] & {
                color: ${colors.skyBlue};
              }
              text-align: center;
              margin: 0;
              margin-bottom: ${spacing.centi};
            `}
          >
            {t('subscription.skipOfferSubtitle', { days: freeTrialDays })}
          </p>
        )}

        {/* Description */}
        <p
          css={css`
            ${fontStyles.horse}
            font-weight: 400;
            color: ${colors.silver};
            text-align: center;
            max-width: 320px;
            margin: 0;
            margin-bottom: ${spacing.deka};
          `}
        >
          {isSkipUser ? t('subscription.skipOfferDesc') : t('subscription.oneTimeOfferDesc')}
        </p>

        {/* Discount card */}
        <div
          css={css`
            position: relative;
            border: 1px solid ${colors.cloud};
            border-radius: 12px;
            padding: 24px;
            width: 100%;
            max-width: 300px;
            background: ${colors.fog};
            html[data-theme='dark'] & {
              background: ${colors.tungsten};
            }
            margin-bottom: ${spacing.deka};
          `}
        >
          {/* 50% OFF badge */}
          <span
            css={css`
              position: absolute;
              top: -12px;
              right: 12px;
              background: ${colors.petrolBlue};
              color: ${colors.sugar};
              ${fontStyles.roboMouse}
              text-transform: uppercase;
              padding: 4px 12px;
              border-radius: 16px;
            `}
          >
            {t('subscription.savePercent', { percent: savingsPercentage })}
          </span>

          {/* Annual Plan label */}
          <div
            css={css`
              ${fontStyles.cat}
              color: ${colors.silver};
              margin-bottom: ${spacing.centi};
              text-align: center;
              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            {t('subscription.yearlyPlan')}
          </div>

          {/* Price row */}
          <div
            css={css`
              display: flex;
              align-items: baseline;
              justify-content: center;
              gap: ${spacing.centi};
            `}
          >
            <span
              css={css`
                ${fontStyles.shark}
                color: ${colors.liquorice};
                html[data-theme='dark'] & {
                  color: ${colors.sugar};
                }
              `}
            >
              {currencySymbol}
              {discountAnnualPrice} {t('subscription.perYearAbbr')}
            </span>
            <span
              css={css`
                ${fontStyles.cat}
                color: ${colors.silver};
                text-decoration: line-through;
              `}
            >
              {currencySymbol}
              {originalAnnualPrice}
            </span>
          </div>
          {isSkipUser && (
            <div
              css={css`
                ${fontStyles.cat}
                color: ${colors.silver};
                text-align: center;
                margin-top: 4px;
              `}
            >
              {t('subscription.afterFreeTrial')}
            </div>
          )}
        </div>

        {/* CTA Button */}
        <button
          onClick={onRedeemOffer}
          disabled={isLoading || isRedeemDisabled}
          css={css`
            width: 100%;
            max-width: 300px;
            height: 56px;
            background: ${colors.petrolBlue};
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 18px;
            font-weight: 500;
            cursor: pointer;
            margin-bottom: ${spacing.centi};
            &:hover {
              opacity: 0.9;
            }
            &:active {
              opacity: 0.8;
            }
            &:disabled {
              opacity: 0.6;
              cursor: not-allowed;
            }
          `}
        >
          {isLoading
            ? t('subscription.processing')
            : isSkipUser
              ? t('subscription.startFreeTrial')
              : t('subscription.redeemOfferNow')}
        </button>

        {/* Fine print */}
        <p
          css={css`
            ${fontStyles.thinFlea}
            color: ${colors.silver};
            text-align: center;
            margin: 0;
            margin-bottom: ${spacing.deci};
          `}
        >
          {isSkipUser
            ? t('subscription.freeTrialFinePrint', { days: freeTrialDays })
            : t('subscription.cancelAnytimeInSettings')}
        </p>

        {/* Explore free version link */}
        <button
          onClick={onExploreFree}
          disabled={isLoading}
          css={css`
            background: none;
            border: none;
            cursor: pointer;
            ${fontStyles.cat}
            color: ${colors.petrolBlue};
            html[data-theme='dark'] & {
              color: ${colors.skyBlue};
            }
            &:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }
          `}
        >
          {t('subscription.exploreFreeVersion')}
        </button>
      </div>
    </div>
  );
};
